import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { verifyResetCode, resetPasswordWithCode } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Key, ArrowLeft } from 'lucide-react';
import LoginNetwork from '../components/LoginNetwork';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const refFromUrl = searchParams.get('ref'); // Referral admin ID from URL
  
  // Persist ref in sessionStorage in case of navigation
  const [refParam, setRefParam] = useState(() => {
    const urlRef = searchParams.get('ref');
    if (urlRef) {
      sessionStorage.setItem('registration_ref', urlRef);
      return urlRef;
    }
    return sessionStorage.getItem('registration_ref') || null;
  });
  
  // Update ref if URL changes
  useEffect(() => {
    if (refFromUrl) {
      sessionStorage.setItem('registration_ref', refFromUrl);
      setRefParam(refFromUrl);
    }
  }, [refFromUrl]);
  
  const [isLogin, setIsLogin] = useState(!refParam); // If ref param exists, default to register
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  
  // Reset password state
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1 = enter code, 2 = enter new password
  const [resetData, setResetData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const { login, register } = useAuth();

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!resetData.email || !resetData.code) {
      toast.error('Inserisci email e codice');
      return;
    }
    
    setLoading(true);
    try {
      await verifyResetCode(resetData.email, resetData.code);
      setResetStep(2);
      toast.success('Codice valido! Inserisci la nuova password');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice non valido o scaduto');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetData.newPassword !== resetData.confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }
    if (resetData.newPassword.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    
    setLoading(true);
    try {
      await resetPasswordWithCode(resetData.email, resetData.code, resetData.newPassword);
      toast.success('Password aggiornata! Ora puoi accedere');
      // Reset state and go back to login
      setShowResetForm(false);
      setResetStep(1);
      setResetData({ email: '', code: '', newPassword: '', confirmPassword: '' });
      setFormData({ ...formData, email: resetData.email });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset della password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        // Clear ref from sessionStorage before login to prevent redirect issues
        sessionStorage.removeItem('registration_ref');
        await login(formData.email, formData.password);
        toast.success('Accesso effettuato con successo');
        // Force redirect to base URL to clear any ref params
        if (window.location.search.includes('ref=')) {
          window.location.href = '/';
        }
      } else {
        if (!formData.name.trim()) {
          toast.error('Inserisci il tuo nome');
          return;
        }
        await register(formData.name, formData.email, formData.password, refParam);
        // Clear ref from sessionStorage after successful registration
        sessionStorage.removeItem('registration_ref');
        toast.success('Registrazione completata');
        // Force redirect to base URL to clear any ref params
        window.location.href = '/';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Si è verificato un errore');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'bg-[#111827] border-gray-800 text-white placeholder:text-gray-500 focus:ring-[#26A17B] focus:border-[#26A17B]';

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4 relative overflow-hidden">
      <div className="login-corner-glow login-breathe" aria-hidden="true" />
      <LoginNetwork />

      <div className="login-card anim-rise relative z-10" data-testid="login-card">
        <img
          src="/icons/ht-logo-v3.png"
          alt="HT"
          className="login-logo anim-zoom mx-auto block"
          data-testid="login-logo"
        />

        <div className="mt-8">
          {/* Referral Banner - Show when registering with a ref code */}
          {!isLogin && !showResetForm && refParam && (
            <div className="bg-gradient-to-r from-yellow-900/30 to-amber-900/20 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <p className="text-yellow-400 text-sm font-medium">
                Sei stato invitato da un amministratore
              </p>
              <p className="text-gray-400 text-xs mt-1">
                Completa la registrazione per unirti alla sua rete
              </p>
            </div>
          )}

          {/* Reset Password Form */}
          {showResetForm ? (
            <div className="space-y-6">
              {resetStep === 1 ? (
                <form onSubmit={handleVerifyCode} className="space-y-5 login-stagger">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email" className="text-gray-300">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="nome@esempio.com"
                      value={resetData.email}
                      onChange={(e) => setResetData({ ...resetData, email: e.target.value })}
                      className={inputClass}
                      required
                      data-testid="reset-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reset-code" className="text-gray-300">Codice di reset</Label>
                    <Input
                      id="reset-code"
                      type="text"
                      placeholder="RT-XXXX-XX"
                      value={resetData.code}
                      onChange={(e) => setResetData({ ...resetData, code: e.target.value.toUpperCase() })}
                      className={`${inputClass} font-mono tracking-wider`}
                      required
                      data-testid="reset-code-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium py-3 rounded-lg"
                    data-testid="verify-code-btn"
                  >
                    {loading ? 'Verifica in corso...' : 'Verifica codice'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-5 login-stagger">
                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-gray-300">Nuova password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={resetData.newPassword}
                        onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                        className={`${inputClass} pr-10`}
                        required
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-gray-300">Conferma password</Label>
                    <Input
                      id="confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={resetData.confirmPassword}
                      onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                      className={inputClass}
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-usdt hover:bg-usdt/90 text-usdt-ink font-medium py-3 rounded-lg"
                    data-testid="reset-password-btn"
                  >
                    {loading ? 'Aggiornamento...' : 'Aggiorna password'}
                  </Button>
                </form>
              )}

              <button
                type="button"
                onClick={() => {
                  setShowResetForm(false);
                  setResetStep(1);
                  setResetData({ email: '', code: '', newPassword: '', confirmPassword: '' });
                }}
                className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mx-auto login-after-1"
                data-testid="reset-back-btn"
              >
                <ArrowLeft size={16} />
                Torna al login
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5 login-stagger">
                {!isLogin && (
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-300">Nome</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Il tuo nome"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={inputClass}
                      data-testid="register-name-input"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="nome@esempio.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={inputClass}
                    required
                    data-testid="auth-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`${inputClass} pr-10`}
                      required
                      data-testid="auth-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="ht-btn ht-btn-primary w-full justify-center py-3"
                  data-testid="auth-submit-btn"
                >
                  {loading ? 'Caricamento...' : isLogin ? 'Accedi' : 'Registrati'}
                </button>
              </form>

              {/* Reset password link - only show on login */}
              {isLogin && (
                <button
                  type="button"
                  onClick={() => setShowResetForm(true)}
                  className="flex items-center gap-2 text-[#8b919c] hover:text-white text-sm mx-auto login-after-1"
                  data-testid="reset-password-link"
                >
                  <Key size={14} />
                  Ho un codice di reset
                </button>
              )}

              {/* Toggle */}
              <p className="text-center text-gray-400 text-sm login-after-2">
                {isLogin ? "Non hai un account? " : "Hai già un account? "}
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="ht-pos font-medium"
                  data-testid="auth-toggle-btn"
                >
                  {isLogin ? 'Registrati' : 'Accedi'}
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
